import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input, Label, Select } from '@/components/ui/input';
import { SearchPicker } from '@/components/SearchPicker';
import { toast } from 'sonner';
import {
  Upload, FileSpreadsheet, ArrowLeft, ChevronRight, ChevronLeft, Loader2,
  Check, X, Info, Search, Building2, ClipboardList, Play, ShieldCheck,
  CheckCircle2, AlertTriangle, MapPin,
} from 'lucide-react';

// ---------- Types ----------
type MappingKind = 'site_field' | 'survey_answer' | 'meta' | 'ignore';

interface ColumnMapping {
  index: number;
  header: string;
  kind: MappingKind;
  target: string | null;
  confidence: 'high' | 'medium' | 'low';
  hint: string;
}

interface Job {
  id: string;
  filename: string;
  sheet: string;
  row_count: number;
  columns: ColumnMapping[];
  created_at: string;
  status?: string;
  client_id?: string | null;
  client_name_cache?: string | null;
  auto_create_client?: boolean;
  auto_create_client_name?: string | null;
  template_id?: string | null;
  template_version?: number | null;
  template_name_cache?: string | null;
  dry_run_summary?: DryRunSummary | null;
  commit_summary?: CommitSummary | null;
  target_mappings?: Array<{ kind: string; target: string; column_index: number }> | null;
}

interface ClientLite { id: string; name: string; trading_name?: string; broker_name?: string | null; site_count?: number; }
interface TemplateLite { id: string; name: string; current_published_version?: number | null; latest_version?: number; }
interface TemplateVersion { id: string; version: number; status: string; schema: { sections?: Array<{ questions?: Array<{ question_code?: string; code?: string; text?: string; label?: string }> }> } }
interface TemplateDetail { id: string; name: string; versions: TemplateVersion[]; current_published_version?: number | null; latest_version?: number; }

interface DryRunSummary {
  rows_total: number;
  rows_valid: number;
  rows_invalid: number;
  unique_sites_total: number;
  unique_sites_geolocated: number;
  unique_sites_name_only: number;
  unique_sites_geocodable?: number;
  geocode_provider?: string;
  geocode_preview?: Array<{ name: string; address: string; resolved: boolean; latitude: number | null; longitude: number | null; formatted?: string | null }>;
  unique_sites_existing_estimate: number;
  unique_sites_new_estimate: number;
  surveys_to_create: number;
  current_marked_count: number;
  invalid_rows_sample: Array<{ row_index: number; reason: string; sample?: Record<string, unknown> }>;
  site_group_preview: Array<{ trading_name: string; latitude: number | null; longitude: number | null; surveys: number; has_current: boolean }>;
  template_id: string;
  template_name: string;
  template_version: number;
  question_count: number;
  cross_tenant?: {
    target_insurer_id: string | null;
    client_conflicts: Array<{ name: string; insurer_id: string }>;
    site_ref_conflicts: Array<{ insurer_id: string; count: number }>;
  };
}

interface CommitSummary {
  client_id: string;
  client_name: string;
  sites_created: number;
  sites_reused: number;
  instructions_created: number;
  results_created: number;
  current_marked_count: number;
  invalid_rows_count: number;
  sites_geocoded?: number;
  sites_geocode_failed?: number;
  geocode_provider?: string;
}

const KIND_COLOURS: Record<MappingKind, string> = {
  site_field: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  survey_answer: 'bg-raptor-blue/15 text-raptor-blue border-raptor-blue/20',
  meta: 'bg-amber-100 text-amber-800 border-amber-200',
  ignore: 'bg-raptor-navy/10 text-raptor-navy/50 border-raptor-navy/10',
};

const SITE_FIELD_TARGETS = [
  { value: 'name', label: 'Site trading name' },
  { value: 'physical_address', label: 'Physical address' },
  { value: 'physical_address_fallback', label: 'Physical address (fallback)' },
  { value: 'latitude', label: 'Latitude' },
  { value: 'longitude', label: 'Longitude' },
  { value: 'city', label: 'City / town' },
  { value: 'province', label: 'Province' },
  { value: 'site_contact_name', label: 'Site contact name' },
  { value: 'site_contact_role', label: 'Site contact role' },
  { value: 'site_contact_phone', label: 'Site contact phone' },
  { value: 'external_reference', label: 'External reference' },
  { value: 'tag:abm_type', label: 'Tag: ABM type' },
  { value: 'tag:vendor', label: 'Tag: vendor' },
];

const META_TARGETS = [
  { value: 'submitted_at', label: 'Submission date (primary)' },
  { value: 'submitted_at_fallback', label: 'Submission date (fallback)' },
];

// Small stat used across dry-run + commit summaries.
const Stat: React.FC<{ label: string; value: number | string; tone?: 'success' | 'blue' | 'warning' | 'muted' }>
  = ({ label, value, tone = 'muted' }) => {
  const cls = tone === 'success' ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
            : tone === 'blue'   ? 'border-raptor-blue/40 bg-raptor-blue/10 text-raptor-navy'
            : tone === 'warning'? 'border-amber-300 bg-amber-50 text-amber-900'
            : 'border-border/60 bg-raptor-neutral/40 text-raptor-navy';
  return (
    <div className={`rounded-md border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-2xl font-headline">{value}</div>
    </div>
  );
};

// ---------- Mapping section (template-driven) ----------
// Sentinel value used in `targetSelections` when the operator explicitly
// marks a row as having no answer available (an intentional null, not
// an oversight).  This distinguishes "we know there's no data" from
// "we haven't decided yet".
const NO_ANSWER = -1;

interface MappingRow { key: string; label: string; sublabel?: string }
interface MappingSectionProps {
  title: string;
  icon: React.ReactNode;
  filter: string;
  rows: MappingRow[];
  targetSelections: Record<string, number | null>;
  setTargetSelections: React.Dispatch<React.SetStateAction<Record<string, number | null>>>;
  columnChoices: { id: string; index: number; header: string }[];
  columnByIndex: Map<number, { id: string; index: number; header: string }>;
}
const MappingSection: React.FC<MappingSectionProps> = ({
  title, icon, filter, rows, targetSelections, setTargetSelections, columnChoices, columnByIndex,
}) => {
  const filterText = filter.trim().toLowerCase();
  const shown = filterText
    ? rows.filter((r) => r.label.toLowerCase().includes(filterText)
                      || (r.sublabel || '').toLowerCase().includes(filterText))
    : rows;
  if (shown.length === 0) return null;
  // A row counts as resolved when it has an Excel column OR is
  // explicitly marked "No answer".
  const isResolved = (key: string): boolean => {
    const v = targetSelections[key];
    return v !== undefined && v !== null;
  };
  const mapped = rows.filter((r) => isResolved(r.key)).length;
  return (
    <div className="rounded-md border border-border/60 overflow-hidden" data-testid={`mapping-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="bg-raptor-neutral/40 px-3 py-2 flex items-center gap-2 text-xs font-body">
        {icon}
        <span className="font-semibold text-raptor-navy uppercase tracking-wide">{title}</span>
        <span className="text-raptor-navy/50">·</span>
        <span className="text-raptor-navy/60 font-tabular">{mapped}/{rows.length} mapped</span>
      </div>
      <table className="w-full text-sm font-body">
        <tbody>
          {shown.map((row) => {
            const selectedIndex = targetSelections[row.key];
            const isNoAnswer = selectedIndex === NO_ANSWER;
            const hasColumn = typeof selectedIndex === 'number' && selectedIndex >= 0;
            const selectedIdStr = hasColumn ? String(selectedIndex) : null;
            const selectedCol = hasColumn ? columnByIndex.get(selectedIndex as number) : undefined;
            return (
              <tr key={row.key} className="border-t border-border/40" data-testid={`mapping-row-${row.key}`}>
                <td className="py-1.5 px-3 align-top w-1/2">
                  <div className="text-sm text-raptor-navy">{row.label}</div>
                  {row.sublabel && <div className="text-[10px] text-raptor-navy/50 font-tabular">{row.sublabel}</div>}
                </td>
                <td className="py-1.5 px-3 w-1/2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      {isNoAnswer ? (
                        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-body text-amber-900 flex items-center gap-2">
                          <Info size={12} /> No answer &mdash; will import as blank
                        </div>
                      ) : (
                        <SearchPicker<{ id: string; index: number; header: string }>
                          items={columnChoices}
                          selectedId={selectedIdStr}
                          onPick={(c) => setTargetSelections((prev) => ({ ...prev, [row.key]: c ? c.index : null }))}
                          labelFor={(c) => c.header || `(column ${c.index})`}
                          sublabelFor={(c) => `Excel column #${c.index}`}
                          placeholder="— not mapped —"
                          emptyLabel="No matching Excel column."
                          compact
                          testId={`mapping-col-picker-${row.key}`}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => setTargetSelections((prev) => ({
                        ...prev,
                        [row.key]: isNoAnswer ? null : NO_ANSWER,
                      }))}
                      className={`shrink-0 h-7 px-2 rounded-md border text-[11px] font-body transition-colors ${isNoAnswer
                        ? 'bg-amber-100 border-amber-300 text-amber-900 hover:bg-amber-200'
                        : 'bg-white border-border/60 text-raptor-navy/70 hover:border-amber-300 hover:text-amber-900'}`}
                      data-testid={`mapping-no-answer-${row.key}`}
                      title={isNoAnswer ? 'Undo — pick a column instead' : 'Mark this field as having no answer'}
                    >
                      {isNoAnswer ? '✕ Undo' : 'No answer'}
                    </button>
                  </div>
                  {selectedCol && (
                    <div className="text-[10px] text-raptor-navy/50 mt-0.5 font-tabular pl-0.5">
                      column #{selectedCol.index}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const ExcelImportWizardPage: React.FC = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const nav = useNavigate();

  const [uploading, setUploading] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [filter, setFilter] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Step 2 state --------------------------------------------------
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [templates, setTemplates] = useState<TemplateLite[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [selectedVersion, setSelectedVersion] = useState<number | ''>('');
  const [templateDetail, setTemplateDetail] = useState<TemplateDetail | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [savingSettings, setSavingSettings] = useState(false);

  // Step 3 state --------------------------------------------------
  // Legacy per-column overrides retained for backward-compat with the
  // old "column-first" UI (still used for the badge counters below).
  const [overrides, setOverrides] = useState<Record<number, { kind: MappingKind; target: string | null }>>({});
  // NEW template-first mapping: for every target (site field, meta or
  // template question) the operator chooses ONE Excel column (or none).
  // targetSelections key format:
  //   `site:<field>`     e.g. `site:name`
  //   `meta:<key>`       e.g. `meta:submitted_at`
  //   `q:<question_code>`
  const [targetSelections, setTargetSelections] = useState<Record<string, number | null>>({});
  const [autoSuggested, setAutoSuggested] = useState(false);
  const [mappingSectionFilter, setMappingSectionFilter] = useState('');

  // Step 4 state --------------------------------------------------
  const [dryRun, setDryRun] = useState<DryRunSummary | null>(null);
  const [dryRunning, setDryRunning] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<CommitSummary | null>(null);
  const [invalidExpanded, setInvalidExpanded] = useState(false);

  // ---------- Load job (Commit A + persisted step) ----------
  const loadJob = async (id: string): Promise<void> => {
    try {
      const r = await api.get<Job>(`/imports/excel/${id}`);
      setJob(r.data);
      // Restore state from persisted job so back/forward is safe.
      if (r.data.client_id) setSelectedClientId(r.data.client_id);
      if (r.data.template_id) setSelectedTemplateId(r.data.template_id);
      if (r.data.template_version) setSelectedVersion(r.data.template_version);
      if (r.data.dry_run_summary) setDryRun(r.data.dry_run_summary);
      if (r.data.commit_summary) setCommitResult(r.data.commit_summary);
      // Pick starting step
      if (r.data.status === 'committed') setStep(4);
      else if (r.data.status === 'dry_run_ready') setStep(4);
      else if (r.data.status === 'configured') setStep(3);
      else setStep(2);
    } catch {
      toast.error('Could not load import job');
    }
  };

  useEffect(() => {
    if (jobId) void loadJob(jobId);
  }, [jobId]);

  // ---------- Fetch clients + templates once job is present ----------
  useEffect(() => {
    if (!job) return;
    void (async () => {
      try {
        const [cr, tr] = await Promise.all([
          api.get<ClientLite[]>('/clients'),
          api.get<TemplateLite[]>('/templates'),
        ]);
        setClients(cr.data || []);
        setTemplates(tr.data || []);
      } catch {
        toast.error('Could not load clients / templates');
      }
    })();
  }, [job]);

  // Load selected template detail (for version picker + question count)
  useEffect(() => {
    if (!selectedTemplateId) { setTemplateDetail(null); return; }
    void (async () => {
      try {
        const r = await api.get<TemplateDetail>(`/templates/${selectedTemplateId}`);
        setTemplateDetail(r.data);
        if (!selectedVersion) {
          const v = r.data.current_published_version || r.data.latest_version || (r.data.versions?.[0]?.version ?? 1);
          setSelectedVersion(v);
        }
      } catch {
        setTemplateDetail(null);
      }
    })();
  }, [selectedTemplateId]);

  // ---------- Step 1: upload ----------
  const upload = async (): Promise<void> => {
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Choose an .xlsx first'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post<Job>('/imports/excel/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
      });
      toast.success(`Loaded ${r.data.row_count} rows across ${r.data.columns.length} columns`);
      setJob(r.data);
      setStep(2);
      nav(`/insurer/imports/excel/${r.data.id}`, { replace: true });
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Upload failed';
      toast.error(String(msg));
    } finally { setUploading(false); }
  };

  // ---------- Step 2: save settings ----------
  const saveSettings = async (): Promise<void> => {
    if (!job) return;
    if (!selectedClientId) { toast.error('Pick a destination client'); return; }
    if (!selectedTemplateId) { toast.error('Pick a template'); return; }
    setSavingSettings(true);
    try {
      await api.post(`/imports/excel/${job.id}/settings`, {
        client_id: selectedClientId,
        auto_create_client: false,
        auto_create_client_name: null,
        template_id: selectedTemplateId,
        template_version: selectedVersion || undefined,
        mapping_overrides: [],
      });
      toast.success('Import configured');
      await loadJob(job.id);
      setStep(3);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed';
      toast.error(String(msg));
    } finally { setSavingSettings(false); }
  };

  // ---------- Step 3: save mapping overrides ----------
  const saveMappingOverrides = async (): Promise<void> => {
    if (!job) return;
    if (!selectedTemplateId) { toast.error('Template missing'); return; }
    setSavingSettings(true);
    try {
      // Build the target-first payload — one entry per (kind, target)
      // with its chosen column_index. Multiple targets may share a
      // column (e.g. tradingname feeds both site.name and Q1.1).
      // NO_ANSWER (-1) entries are skipped: the field is intentionally
      // left blank on import so the backend keeps its default null.
      const targetMappings: Array<{ kind: MappingKind; target: string; column_index: number }> = [];
      for (const [key, idx] of Object.entries(targetSelections)) {
        if (idx === null || idx === undefined) continue;
        if (idx < 0) continue;  // NO_ANSWER sentinel
        let kind: MappingKind = 'ignore';
        let target = '';
        if (key.startsWith('site:')) { kind = 'site_field'; target = key.slice(5); }
        else if (key.startsWith('meta:')) { kind = 'meta'; target = key.slice(5); }
        else if (key.startsWith('q:')) { kind = 'survey_answer'; target = key.slice(2); }
        if (kind === 'ignore' || !target) continue;
        targetMappings.push({ kind, target, column_index: idx });
      }
      await api.post(`/imports/excel/${job.id}/settings`, {
        client_id: selectedClientId,
        auto_create_client: false,
        auto_create_client_name: null,
        template_id: selectedTemplateId,
        template_version: selectedVersion || undefined,
        target_mappings: targetMappings,
      });
      toast.success(`Mapping saved — ${targetMappings.length} field(s) will be imported`);
      await loadJob(job.id);
      setOverrides({});
      setStep(4);
      void runDryRun();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed';
      toast.error(String(msg));
    } finally { setSavingSettings(false); }
  };

  // ---------- Step 4: dry-run + commit ----------
  const runDryRun = async (): Promise<void> => {
    if (!job) return;
    setDryRunning(true);
    try {
      const r = await api.post<DryRunSummary>(`/imports/excel/${job.id}/dry-run`);
      setDryRun(r.data);
      toast.success('Dry-run complete');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Dry-run failed';
      toast.error(String(msg));
    } finally { setDryRunning(false); }
  };

  const commit = async (): Promise<void> => {
    if (!job) return;
    if (!window.confirm('This will insert sites and survey results into the live database. Continue?')) return;
    setCommitting(true);
    try {
      const r = await api.post<CommitSummary & { ok: boolean; status: string }>(`/imports/excel/${job.id}/commit`);
      setCommitResult(r.data);
      toast.success(`Import complete — ${r.data.results_created} surveys created`);
      await loadJob(job.id);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Commit failed';
      toast.error(String(msg));
    } finally { setCommitting(false); }
  };

  // ---------- Derived data ----------
  const mergedColumns: ColumnMapping[] = useMemo(() => {
    if (!job) return [];
    return job.columns.map((c) => {
      const o = overrides[c.index];
      if (!o) return c;
      return { ...c, kind: o.kind, target: o.target, hint: 'Operator override', confidence: 'high' };
    });
  }, [job, overrides]);

  const counts = useMemo(() => {
    const out = { site_field: 0, survey_answer: 0, meta: 0, ignore: 0 };
    mergedColumns.forEach((c) => { out[c.kind] += 1; });
    return out;
  }, [mergedColumns]);

  const filteredCols = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return mergedColumns;
    return mergedColumns.filter((c) => c.header.toLowerCase().includes(q)
      || (c.target || '').toLowerCase().includes(q)
      || c.hint.toLowerCase().includes(q));
  }, [mergedColumns, filter]);

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return clients.slice(0, 50);
    return clients.filter((c) => (c.name || '').toLowerCase().includes(q)
      || (c.trading_name || '').toLowerCase().includes(q)).slice(0, 50);
  }, [clients, clientSearch]);

  interface QuestionOption { id: string; text: string; section: string; display_number: string }
  const questionOptions: QuestionOption[] = useMemo(() => {
    if (!templateDetail || !selectedVersion) return [];
    const v = templateDetail.versions?.find((x) => x.version === Number(selectedVersion));
    if (!v) return [];
    const out: QuestionOption[] = [];
    // Widen the schema type here — templates carry section titles + question
    // text/label/display_number we can surface as sublabel.
    type LooseQ = { question_code?: string; code?: string; text?: string; label?: string; display_number?: string };
    type LooseSec = { title?: string; name?: string; questions?: LooseQ[] };
    ((v.schema as unknown as { sections?: LooseSec[] })?.sections || []).forEach((sec) => {
      const secName = sec.title || sec.name || '';
      (sec.questions || []).forEach((q) => {
        const code = q.question_code || q.code;
        if (!code) return;
        const text = q.text || q.label || '';
        const num = q.display_number || '';
        out.push({
          id: String(code),
          text: `${num ? num + ' ' : ''}${text}`.trim(),
          section: secName,
          display_number: num,
        });
      });
    });
    return out;
  }, [templateDetail, selectedVersion]);
  const questionCodes: string[] = useMemo(() => questionOptions.map((o) => o.id), [questionOptions]);

  // ---------- Column choices used by target pickers ----------
  interface ColumnChoice { id: string; index: number; header: string }
  const columnChoices: ColumnChoice[] = useMemo(() => {
    if (!job) return [];
    return (job.columns || []).map((c) => ({
      id: String(c.index),
      index: c.index,
      header: c.header || '',
    }));
  }, [job]);

  // ---------- Auto-suggest: match each target to a best-fit Excel column ----------
  // Strategy: always compute a fresh, deterministic suggestion from the
  // Excel headers ↔ template.  Then layer any *explicit* operator picks
  // (site_field / meta / persisted-via-new-UI survey_answer) on top so
  // manual overrides survive re-loads.  Fires once per (job + template).
  useEffect(() => {
    if (autoSuggested) return;
    if (!job || !questionOptions.length) return;

    // --------- Fresh auto-suggest ---------------------------------
    const norm = (s: string): string => s.toLowerCase()
      .replace(/[_\-\s]+/g, ' ')
      .replace(/[^a-z0-9. ]+/g, ' ')
      .trim();
    const cols = (job.columns || []).map((c) => ({
      index: c.index,
      header: c.header || '',
      h: norm(c.header || ''),
    }));
    const scoreHeader = (h: string, needles: string[]): number => {
      let s = 0;
      for (const n of needles) {
        if (!n) continue;
        const nn = norm(n);
        if (!nn) continue;
        if (h === nn) s += 500;
        else if (h.includes(nn)) s += 250 - Math.min(200, Math.abs(h.length - nn.length));
        else {
          const toks = nn.split(' ').filter(Boolean);
          if (toks.length && toks.every((tok) => tok && h.includes(tok))) s += 80;
        }
      }
      return s;
    };
    const bestFor = (needles: string[], exclude: Set<number>): { idx: number | null; score: number } => {
      let bestIdx: number | null = null;
      let bestScore = 0;
      for (const c of cols) {
        if (exclude.has(c.index)) continue;
        const s = scoreHeader(c.h, needles);
        if (s > bestScore) { bestScore = s; bestIdx = c.index; }
      }
      return { idx: bestScore >= 80 ? bestIdx : null, score: bestScore };
    };

    const suggestion: Record<string, number | null> = {};
    // Note: unlike a naive "one column → one target" model, one Excel
    // column can legitimately feed multiple targets (e.g. the "1.1
    // tradingname" column populates both the site's trading name AND
    // the survey answer to question 1.1).  So we do NOT exclude a
    // column once it's been chosen — every target gets its independent
    // best match.

    // Site fields (curated).
    const SITE_NEEDLES: Record<string, string[]> = {
      name: ['tradingname', 'trading name', 'site name', 'store name', 'branch name'],
      physical_address: ['clientinformationaddress', 'client information address', 'physical address', 'site address', 'address'],
      latitude: ['deps_lat', 'latitude', 'lat', 'gpslat', 'gps lat'],
      longitude: ['deps_lon', 'longitude', 'longitude', 'lon', 'lng', 'gpslon'],
      city: ['deps_city', 'city', 'town'],
      province: ['deps_province', 'province', 'state', 'region'],
      site_contact_name: ['contactname', 'contact name', 'site contact name'],
      site_contact_phone: ['contactphone', 'contact phone', 'phone', 'mobile'],
      external_reference: ['inspectionnumber', 'external reference', 'red number', 'site reference'],
    };
    for (const [key, needles] of Object.entries(SITE_NEEDLES)) {
      const r = bestFor(needles, new Set());
      if (r.idx !== null) suggestion[`site:${key}`] = r.idx;
    }
    // Submitted at
    const subRes = bestFor(['inspectiondate', 'submitted at', 'survey date', 'inspection date', 'workcompleted', 'date'], new Set());
    if (subRes.idx !== null) suggestion['meta:submitted_at'] = subRes.idx;

    // Template questions — match on the MEANING of the header text, NOT its
    // leading number. The Excel's numbering (e.g. "1.4 Insured value") often
    // differs from the template's (e.g. "1.2 Insured value"), so number-only
    // matching mis-binds columns and misses fields entirely. We strip any
    // leading "1.4" / "UW.1" token from both sides, score by text similarity
    // (also against the question_code's last segment, e.g. INSURED_VALUE →
    // "insured value"), add a small bonus only when the numbers DO agree, then
    // greedily assign the strongest matches (each question + each column once).
    const stripNum = (s: string): { num: string; text: string } => {
      const mm = /^([a-z]*\d+(?:\.\d+)*)\s+(.*)$/.exec(s.trim());
      return mm ? { num: mm[1], text: mm[2].trim() } : { num: '', text: s.trim() };
    };
    const textScore = (a: string, b: string): number => {
      if (!a || !b) return 0;
      if (a === b) return 1000;
      // Substring match only counts when the shorter side is substantial —
      // otherwise tiny tokens like "type"/"lon" produce false positives.
      if ((a.includes(b) || b.includes(a)) && Math.min(a.length, b.length) >= 5) {
        return 620 - Math.min(300, Math.abs(a.length - b.length) * 4);
      }
      const at = a.split(' ').filter(Boolean);
      const bt = b.split(' ').filter(Boolean);
      const setB = new Set(bt);
      const common = at.filter((t) => setB.has(t)).length;
      if (common === 0) return 0;
      return Math.round((common / Math.max(at.length, bt.length)) * 520);
    };
    const usedByQuestions = new Set<number>();
    const pairs: Array<{ qid: string; idx: number; score: number }> = [];
    for (const q of questionOptions) {
      const qText = stripNum(norm(q.text || '')).text;
      const codeTail = norm((q.id || '').split('.').pop() || '');
      const qnum = (q.display_number || '').trim();
      for (const c of cols) {
        const cs = stripNum(c.h);
        let s = Math.max(textScore(qText, cs.text), textScore(codeTail, cs.text));
        if (qnum && cs.num && qnum === cs.num) s += 150;   // confirming bonus only
        if (s > 0) pairs.push({ qid: q.id, idx: c.index, score: s });
      }
    }
    pairs.sort((a, b) => b.score - a.score);
    const assignedQ = new Set<string>();
    const MIN_Q_SCORE = 260;
    for (const p of pairs) {
      if (p.score < MIN_Q_SCORE) break;
      if (assignedQ.has(p.qid) || usedByQuestions.has(p.idx)) continue;
      suggestion[`q:${p.qid}`] = p.idx;
      assignedQ.add(p.qid);
      usedByQuestions.add(p.idx);
    }

    // --------- Layer explicit operator picks over the top ---------
    // Preferred: the NEW target-first `target_mappings` stored by the
    // rebuilt Step 3 (supports one column feeding multiple targets).
    const targetMappings = (job.target_mappings || []) as Array<{ kind: string; target: string; column_index: number }>;
    if (targetMappings.length > 0) {
      for (const m of targetMappings) {
        const prefix = m.kind === 'site_field' ? 'site:'
                    : m.kind === 'meta' ? 'meta:'
                    : m.kind === 'survey_answer' ? 'q:' : '';
        if (!prefix || !m.target) continue;
        suggestion[`${prefix}${m.target}`] = m.column_index;
      }
    } else {
      // Legacy fallback — only trust site_field + meta rows.
      const persisted = (job.columns || []).filter((c) => c.kind !== 'ignore' && c.target);
      if (persisted.length > 0) {
        persisted.forEach((c) => {
          if (c.kind === 'site_field') suggestion[`site:${c.target}`] = c.index;
          else if (c.kind === 'meta') suggestion[`meta:${c.target}`] = c.index;
        });
      }
    }

    setTargetSelections(suggestion);
    setAutoSuggested(true);
  }, [job, questionOptions, autoSuggested]);

  const columnByIndex = useMemo(() => {
    const m = new Map<number, ColumnChoice>();
    columnChoices.forEach((c) => m.set(c.index, c));
    return m;
  }, [columnChoices]);

  // Group questions by their section for the mapping UI.
  interface QuestionSection { title: string; questions: QuestionOption[] }
  const questionSections: QuestionSection[] = useMemo(() => {
    const map = new Map<string, QuestionOption[]>();
    for (const q of questionOptions) {
      const key = q.section || 'General';
      const arr = map.get(key) || [];
      arr.push(q);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([title, questions]) => ({ title, questions }));
  }, [questionOptions]);

  const mappedCount = useMemo(() =>
    Object.values(targetSelections).filter((v) => v !== null && v !== undefined).length,
  [targetSelections]);
  // Visible mapping targets = 2 Report-info rows + N template questions.
  // Hidden site fields (name/address/lat/lng/etc.) auto-fill silently
  // from the survey answers or from Excel headers.
  const visibleTargetCount = useMemo(() => 2 + questionOptions.length, [questionOptions.length]);
  const visibleMappedCount = useMemo(() => {
    let n = 0;
    if (targetSelections['meta:submitted_at'] != null) n += 1;
    if (targetSelections['site:external_reference'] != null) n += 1;
    for (const q of questionOptions) {
      if (targetSelections[`q:${q.id}`] != null) n += 1;
    }
    return n;
  }, [targetSelections, questionOptions]);
  const unmatchedTargetsCount = Math.max(0, visibleTargetCount - visibleMappedCount);
  const unusedColumnsCount = useMemo(() => {
    if (!job) return 0;
    const used = new Set(
      Object.values(targetSelections)
        .filter((v): v is number => typeof v === 'number' && v >= 0),
    );
    return (job.columns || []).length - used.size;
  }, [job, targetSelections]);

  const isCommitted = job?.status === 'committed' || Boolean(commitResult);

  // ---------- Stepper header ----------
  const StepperHeader: React.FC = () => (
    <div className="flex items-center gap-2 text-xs font-body" data-testid="wizard-stepper">
      {[
        { n: 1, label: 'Upload', icon: <Upload size={12} /> },
        { n: 2, label: 'Client & template', icon: <Building2 size={12} /> },
        { n: 3, label: 'Mapping', icon: <ClipboardList size={12} /> },
        { n: 4, label: 'Dry-run & commit', icon: <ShieldCheck size={12} /> },
      ].map((s, i) => (
        <React.Fragment key={s.n}>
          <div className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border ${step === s.n
            ? 'bg-raptor-blue text-white border-raptor-blue'
            : step > s.n
              ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
              : 'bg-white text-raptor-navy/70 border-border/60'}`}>
            {step > s.n ? <Check size={12} /> : s.icon}
            <span className="hidden sm:inline">{s.label}</span>
          </div>
          {i < 3 && <ChevronRight size={12} className="text-raptor-navy/40" />}
        </React.Fragment>
      ))}
    </div>
  );

  // ---------- Render ----------
  return (
    <div className="space-y-5 animate-fade-in" data-testid="excel-import-wizard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <button
            onClick={() => nav('/insurer/imports')}
            className="text-xs text-raptor-navy/60 hover:text-raptor-blue inline-flex items-center gap-1"
            data-testid="back-to-imports"
          >
            <ArrowLeft size={12} /> All imports
          </button>
          <h2 className="font-headline text-2xl text-raptor-navy tracking-wide mt-1 flex items-center gap-2">
            <FileSpreadsheet size={20} className="text-raptor-blue" /> Excel bulk import
          </h2>
          <p className="text-sm text-raptor-navy/60 font-body max-w-2xl">
            Upload a spreadsheet of sites + survey answers. We&rsquo;ll parse the headers, help you map columns, dry-run the outcome, then commit sites and pre-filled surveys in one step.
          </p>
        </div>
        {job && <StepperHeader />}
        {!job && (
          <div className="flex items-center gap-2 text-xs font-body" data-testid="wizard-stepper">
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border bg-raptor-blue text-white border-raptor-blue">
              <Upload size={12} /> <span className="hidden sm:inline">Upload</span>
            </div>
            <ChevronRight size={12} className="text-raptor-navy/40" />
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border bg-white text-raptor-navy/70 border-border/60">
              <Building2 size={12} /> <span className="hidden sm:inline">Client &amp; template</span>
            </div>
            <ChevronRight size={12} className="text-raptor-navy/40" />
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border bg-white text-raptor-navy/70 border-border/60">
              <ClipboardList size={12} /> <span className="hidden sm:inline">Mapping</span>
            </div>
            <ChevronRight size={12} className="text-raptor-navy/40" />
            <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 border bg-white text-raptor-navy/70 border-border/60">
              <ShieldCheck size={12} /> <span className="hidden sm:inline">Dry-run &amp; commit</span>
            </div>
          </div>
        )}
      </div>

      {/* ================================================================
          STEP 1 — Upload
         ================================================================ */}
      {(!job || step === 1) && (
        <Card data-testid="step-1-upload">
          <CardHeader>
            <CardTitle className="text-base">Step 1 &mdash; Upload workbook</CardTitle>
            <CardDescription>Drop your .xlsx (max 100 MB, up to ~50,000 rows). We&rsquo;ll parse headers and stage the file. Nothing is written to the database yet.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed border-border/60 rounded-md p-8 text-center">
              <FileSpreadsheet size={40} className="mx-auto text-raptor-blue/60 mb-2" />
              <p className="text-sm font-body text-raptor-navy/70 mb-3">Choose an .xlsx file to stage.</p>
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx"
                className="mx-auto block text-sm font-body"
                data-testid="excel-file-input"
              />
              <Button
                variant="gradient"
                className="mt-4"
                onClick={() => void upload()}
                disabled={uploading}
                data-testid="excel-upload-btn"
              >
                {uploading ? <><Loader2 size={14} className="animate-spin mr-2" /> Parsing&hellip;</> : <><Upload size={14} className="mr-2" /> Stage file</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================================================================
          STEP 2 — Client + Template picker
         ================================================================ */}
      {job && step === 2 && (
        <Card data-testid="step-2-client-template">
          <CardHeader>
            <CardTitle className="text-base">Step 2 &mdash; Choose client &amp; survey template</CardTitle>
            <CardDescription>Every row in <b>{job.filename}</b> ({job.row_count.toLocaleString()} rows) will be attached to the chosen client and scored against the chosen survey template.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* --- Client --- */}
            <div className="space-y-2">
              <Label>Destination client</Label>
              <div className="relative max-w-md">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
                <Input
                  className="h-8 text-sm pl-7"
                  placeholder="Search clients…"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  data-testid="client-search"
                />
              </div>
              <div className="max-h-52 overflow-auto rounded-md border border-border/60 divide-y divide-border/40">
                {filteredClients.length === 0 && (
                  <div className="px-3 py-2 text-sm text-raptor-navy/50 italic">No clients found.</div>
                )}
                {filteredClients.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setSelectedClientId(c.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-raptor-blue/5 ${selectedClientId === c.id ? 'bg-raptor-blue/10 border-l-4 border-raptor-blue' : ''}`}
                    data-testid={`client-option-${c.id}`}
                  >
                    <div className="font-semibold text-raptor-navy">{c.trading_name || c.name}</div>
                    <div className="text-[11px] text-raptor-navy/60">{c.broker_name ? `Broker: ${c.broker_name}` : 'No broker'} &middot; {c.site_count ?? 0} site(s)</div>
                  </button>
                ))}
              </div>
            </div>

            {/* --- Template --- */}
            <div className="space-y-2">
              <Label>Survey template</Label>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase text-raptor-navy/60">Template</Label>
                  <Select
                    value={selectedTemplateId}
                    onChange={(e) => { setSelectedTemplateId(e.target.value); setSelectedVersion(''); }}
                    data-testid="template-select"
                  >
                    <option value="">— Choose a template —</option>
                    {templates.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.id})</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label className="text-[11px] uppercase text-raptor-navy/60">Version</Label>
                  <Select
                    value={selectedVersion === '' ? '' : String(selectedVersion)}
                    onChange={(e) => setSelectedVersion(e.target.value ? Number(e.target.value) : '')}
                    disabled={!templateDetail}
                    data-testid="template-version-select"
                  >
                    <option value="">— Choose version —</option>
                    {(templateDetail?.versions || []).map((v) => (
                      <option key={v.version} value={v.version}>
                        v{v.version} &middot; {v.status}{templateDetail?.current_published_version === v.version ? ' ★ published' : ''}
                      </option>
                    ))}
                  </Select>
                </div>
              </div>
              {questionCodes.length > 0 && (
                <div className="text-xs text-raptor-navy/60 font-body flex items-center gap-1">
                  <Info size={12} /> Template has <b>&nbsp;{questionCodes.length}&nbsp;</b> questions. Missing spreadsheet columns will be saved with blank answers.
                </div>
              )}
            </div>

            {/* --- Nav --- */}
            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => nav('/insurer/imports')} data-testid="wizard-cancel">Cancel</Button>
              <Button
                variant="gradient"
                onClick={() => void saveSettings()}
                disabled={savingSettings || !selectedClientId || !selectedTemplateId}
                data-testid="wizard-next-step-2"
              >
                {savingSettings ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : <>Continue to mapping <ChevronRight size={14} className="ml-1" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================================================================
          STEP 3 — Template-driven mapping (pick the Excel column for
          each site field / template question).
         ================================================================ */}
      {job && step === 3 && (
        <Card data-testid="step-3-mapping">
          <CardHeader>
            <CardTitle className="text-base">Step 3 &mdash; Map fields to Excel columns</CardTitle>
            <CardDescription>
              For each field the survey template needs, pick the Excel column its answer lives in. Anything you don&rsquo;t map is simply ignored.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary strip */}
            <div className="flex flex-wrap gap-2 items-center">
              <Badge variant="success" className="text-[11px]" data-testid="mapping-count-mapped">
                <Check size={10} /> Mapped: {visibleMappedCount} / {visibleTargetCount}
              </Badge>
              <Badge variant="warning" className="text-[11px]" data-testid="mapping-count-unmatched">
                <Info size={10} /> Unmapped: {unmatchedTargetsCount}
              </Badge>
              <Badge variant="muted" className="text-[11px]" data-testid="mapping-count-unused">
                <X size={10} /> Excel columns ignored: {unusedColumnsCount}
              </Badge>
              <div className="ml-auto relative max-w-xs w-full">
                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-raptor-navy/40" />
                <Input
                  className="h-8 text-sm pl-7"
                  placeholder="Filter fields…"
                  value={mappingSectionFilter}
                  onChange={(e) => setMappingSectionFilter(e.target.value)}
                  data-testid="mapping-filter"
                />
              </div>
            </div>

            {/* --- Section A: Report metadata (matches PDF header) --- */}
            <MappingSection
              title="Report info"
              icon={<Info size={12} className="text-amber-700" />}
              filter={mappingSectionFilter}
              rows={[
                { key: 'meta:submitted_at', label: 'Inspection / submission date', sublabel: 'Report header' },
                { key: 'site:external_reference', label: 'Inspection number', sublabel: 'Report header' },
              ]}
              targetSelections={targetSelections}
              setTargetSelections={setTargetSelections}
              columnChoices={columnChoices}
              columnByIndex={columnByIndex}
            />

            {/* --- Section B: Survey questions grouped by section --- */}
            {questionSections.length === 0 && (
              <div className="rounded-md border border-border/60 bg-raptor-neutral/30 px-3 py-4 text-sm text-raptor-navy/60 font-body">
                Selected template has no questions.
              </div>
            )}
            {questionSections.map((sec) => (
              <MappingSection
                key={sec.title}
                title={`Survey questions — ${sec.title}`}
                icon={<Check size={12} className="text-raptor-blue" />}
                filter={mappingSectionFilter}
                rows={sec.questions.map((q) => ({
                  key: `q:${q.id}`,
                  label: q.text || q.id,
                  sublabel: `${q.display_number ? q.display_number + ' · ' : ''}${q.id}`,
                }))}
                targetSelections={targetSelections}
                setTargetSelections={setTargetSelections}
                columnChoices={columnChoices}
                columnByIndex={columnByIndex}
              />
            ))}

            <p className="text-[11px] text-raptor-navy/50 font-body">
              Note: the site&rsquo;s trading name and address are taken directly from the answers to Q1.1 and Q1.2. GPS coordinates are auto-detected from the workbook when present — no need to map them here.
            </p>

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)} data-testid="wizard-back-step-3"><ChevronLeft size={14} className="mr-1" /> Back</Button>
              <Button
                variant="gradient"
                onClick={() => void saveMappingOverrides()}
                disabled={savingSettings || visibleMappedCount === 0}
                data-testid="wizard-next-step-3"
              >
                {savingSettings ? <><Loader2 size={14} className="animate-spin mr-2" /> Saving…</> : <>Continue to dry-run <ChevronRight size={14} className="ml-1" /></>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ================================================================
          STEP 4 — Dry-run + Commit
         ================================================================ */}
      {job && step === 4 && (
        <Card data-testid="step-4-commit">
          <CardHeader>
            <CardTitle className="text-base">Step 4 &mdash; Dry-run &amp; commit</CardTitle>
            <CardDescription>
              Preview exactly what will be created.  Nothing is written until you click <b>Commit import</b>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={() => void runDryRun()}
                disabled={dryRunning || isCommitted}
                data-testid="wizard-run-dry-run"
              >
                {dryRunning ? <><Loader2 size={14} className="animate-spin mr-2" /> Analysing…</> : <><Play size={14} className="mr-2" /> {dryRun ? 'Re-run dry-run' : 'Run dry-run'}</>}
              </Button>
              <div className="text-[11px] text-raptor-navy/50 font-body">Job status: <b>{job.status || 'staged'}</b></div>
            </div>

            {dryRun && (
              <div className="space-y-4">
                {dryRun.cross_tenant && (dryRun.cross_tenant.client_conflicts.length > 0 || dryRun.cross_tenant.site_ref_conflicts.length > 0) && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-1.5" data-testid="cross-tenant-warning">
                    <div className="flex items-center gap-2 text-amber-800 font-semibold text-sm">
                      <Info size={15} /> Heads-up: same name exists in another portal
                    </div>
                    <p className="text-xs text-amber-900/80">
                      This import goes <b>only</b> into <b>{dryRun.cross_tenant.target_insurer_id}</b> and will not read, change, or merge any other tenant&rsquo;s data — the portals stay fully isolated. We just noticed the same client/site also exists under another insurer (which is perfectly normal — the same business can be insured by two insurers). This is only here so you can confirm you picked the right portal.
                    </p>
                    {dryRun.cross_tenant.client_conflicts.map((c, i) => (
                      <div key={`cc-${i}`} className="text-xs text-amber-900/80">• A separate client &ldquo;{c.name}&rdquo; also exists under <b>{c.insurer_id}</b> (untouched by this import)</div>
                    ))}
                    {dryRun.cross_tenant.site_ref_conflicts.map((s, i) => (
                      <div key={`sc-${i}`} className="text-xs text-amber-900/80">• {s.count} site reference(s) are also used under <b>{s.insurer_id}</b> (untouched by this import)</div>
                    ))}
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Stat label="Rows valid" value={dryRun.rows_valid} tone="success" />
                  <Stat label="Rows invalid" value={dryRun.rows_invalid} tone={dryRun.rows_invalid > 0 ? 'warning' : 'muted'} />
                  <Stat label="Unique sites" value={dryRun.unique_sites_total} tone="blue" />
                  <Stat label="Surveys to create" value={dryRun.surveys_to_create} tone="blue" />
                  <Stat label="Sites new (est.)" value={dryRun.unique_sites_new_estimate} tone="success" />
                  <Stat label="Sites reused (est.)" value={dryRun.unique_sites_existing_estimate} tone="muted" />
                  <Stat label="Marked current" value={dryRun.current_marked_count} tone="success" />
                  <Stat label="Questions per survey" value={dryRun.question_count} tone="muted" />
                </div>

                {(dryRun.unique_sites_name_only > 0 || (dryRun.unique_sites_geocodable || 0) > 0) && (
                  <div className="rounded-md border border-border/60 bg-raptor-neutral/20 p-3 space-y-2" data-testid="geocode-panel">
                    <div className="flex items-center gap-2 text-sm font-semibold text-raptor-navy">
                      <MapPin size={15} /> Address → coordinates
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Stat label="Already geolocated" value={dryRun.unique_sites_geolocated} tone="success" />
                      <Stat label="Will geocode from address" value={dryRun.unique_sites_geocodable || 0} tone="blue" />
                      <Stat label="No coords & no address" value={Math.max(0, dryRun.unique_sites_name_only - (dryRun.unique_sites_geocodable || 0))} tone={(dryRun.unique_sites_name_only - (dryRun.unique_sites_geocodable || 0)) > 0 ? 'warning' : 'muted'} />
                      <Stat label="Provider" value={dryRun.geocode_provider === 'google_maps' ? 'Google' : 'OpenStreetMap'} tone="muted" />
                    </div>
                    {dryRun.geocode_preview && dryRun.geocode_preview.length > 0 && (
                      <div className="rounded border border-border/50 bg-white overflow-hidden">
                        <div className="px-3 py-1.5 text-[10px] uppercase text-raptor-navy/60 bg-raptor-neutral/40">Sample resolved addresses</div>
                        <table className="w-full text-xs font-body">
                          <tbody>
                            {dryRun.geocode_preview.map((g, i) => (
                              <tr key={i} className="border-t border-border/40">
                                <td className="py-1.5 px-3 font-semibold text-raptor-navy truncate max-w-[160px]">{g.name}</td>
                                <td className="py-1.5 px-3 text-raptor-navy/60 truncate max-w-[220px]">{g.formatted || g.address}</td>
                                <td className="py-1.5 px-3 text-right font-tabular">
                                  {g.resolved
                                    ? <span className="text-emerald-600">{g.latitude?.toFixed(4)}, {g.longitude?.toFixed(4)}</span>
                                    : <span className="text-amber-600">not found</span>}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <p className="text-[11px] text-raptor-navy/50 font-body">
                      On commit, address-only sites are geocoded ({dryRun.geocode_provider === 'google_maps' ? 'Google Maps' : 'free OpenStreetMap'}). Any that can&rsquo;t be resolved stay fillable later via Analytics → Geocode sites.
                    </p>
                  </div>
                )}

                {dryRun.rows_invalid > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50">
                    <button
                      type="button"
                      className="w-full text-left px-3 py-2 text-sm font-body flex items-center gap-2 text-amber-900 hover:bg-amber-100"
                      onClick={() => setInvalidExpanded((v) => !v)}
                      data-testid="invalid-toggle"
                    >
                      <AlertTriangle size={14} /> {dryRun.rows_invalid} invalid row(s) (skipped) &mdash; click to {invalidExpanded ? 'collapse' : 'expand'}
                    </button>
                    {invalidExpanded && (
                      <div className="px-3 pb-3 max-h-64 overflow-auto">
                        <table className="w-full text-xs font-body">
                          <thead className="text-amber-900/70">
                            <tr><th className="text-left py-1">Row</th><th className="text-left py-1">Reason</th><th className="text-left py-1">Sample</th></tr>
                          </thead>
                          <tbody>
                            {dryRun.invalid_rows_sample.map((r) => (
                              <tr key={r.row_index} className="border-t border-amber-200">
                                <td className="py-1 pr-2 font-tabular">{r.row_index}</td>
                                <td className="py-1 pr-2">{r.reason}</td>
                                <td className="py-1 pr-2 font-tabular text-[10px] text-amber-900/60">{JSON.stringify(r.sample || {})}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {dryRun.site_group_preview.length > 0 && (
                  <div className="rounded-md border border-border/60">
                    <div className="px-3 py-2 text-xs uppercase text-raptor-navy/60 font-body bg-raptor-neutral/40">Site group preview (first 20)</div>
                    <div className="max-h-64 overflow-auto">
                      <table className="w-full text-xs font-body">
                        <thead className="text-raptor-navy/70">
                          <tr>
                            <th className="text-left py-1 px-3">Trading name</th>
                            <th className="text-left py-1 px-3">Lat / Lng</th>
                            <th className="text-left py-1 px-3">Surveys</th>
                            <th className="text-left py-1 px-3">Current?</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dryRun.site_group_preview.map((s, i) => (
                            <tr key={`grp-${i}-${s.trading_name}`} className="border-t border-border/60">
                              <td className="py-1 px-3 font-tabular text-raptor-navy">{s.trading_name}</td>
                              <td className="py-1 px-3 font-tabular">{s.latitude ?? '—'}, {s.longitude ?? '—'}</td>
                              <td className="py-1 px-3 font-tabular">{s.surveys}</td>
                              <td className="py-1 px-3">{s.has_current ? <CheckCircle2 size={12} className="text-emerald-600" /> : <X size={12} className="text-raptor-navy/40" />}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {commitResult && (
              <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4 space-y-2" data-testid="commit-result">
                <div className="flex items-center gap-2 text-emerald-900 font-semibold">
                  <CheckCircle2 size={16} /> Import committed successfully
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Stat label="Sites created" value={commitResult.sites_created} tone="success" />
                  <Stat label="Sites reused" value={commitResult.sites_reused} tone="muted" />
                  <Stat label="Surveys created" value={commitResult.results_created} tone="success" />
                  <Stat label="Marked current" value={commitResult.current_marked_count} tone="success" />
                  {(commitResult.sites_geocoded || 0) > 0 && (
                    <Stat label="Geocoded from address" value={commitResult.sites_geocoded || 0} tone="blue" />
                  )}
                  {(commitResult.sites_geocode_failed || 0) > 0 && (
                    <Stat label="Geocode unresolved" value={commitResult.sites_geocode_failed || 0} tone="warning" />
                  )}
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" onClick={() => nav(`/insurer/clients/${commitResult.client_id}?tab=sites`)} data-testid="open-client-btn">Open client &rarr;</Button>
                  <Button size="sm" variant="ghost" onClick={() => nav('/insurer/imports')} data-testid="back-imports-btn">All imports</Button>
                </div>
              </div>
            )}

            {!isCommitted && (
              <div className="flex items-center justify-between pt-2">
                <Button variant="ghost" onClick={() => setStep(3)} data-testid="wizard-back-step-4"><ChevronLeft size={14} className="mr-1" /> Back to mapping</Button>
                <Button
                  variant="gradient"
                  onClick={() => void commit()}
                  disabled={!dryRun || committing || dryRun.rows_valid === 0}
                  data-testid="wizard-commit"
                >
                  {committing ? <><Loader2 size={14} className="animate-spin mr-2" /> Committing…</> : <>Commit import <Check size={14} className="ml-1" /></>}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ExcelImportWizardPage;
